import { useMemo } from "react";
import type { FactorEntry } from "../services/productFactorsService";
import { computeRiskMetrics, type RiskMetrics } from "../utils/riskMetrics";

export type AnalyzerOverallRiskLabel =
  | "Conservative"
  | "Low-Moderate"
  | "Moderate"
  | "Moderate-Aggressive"
  | "Aggressive";

export interface AnalyzerOverallRisk {
  level: number;
  label: AnalyzerOverallRiskLabel;
}

export interface AnalyzerRiskAnalysis {
  metrics: RiskMetrics | null;
  benchmarkMetrics: RiskMetrics | null;
  dailyMove: number | null;
  benchmarkDailyMove: number | null;
  overallRisk: AnalyzerOverallRisk | null;
}

function usableMetrics(metrics: RiskMetrics): RiskMetrics | null {
  return metrics.volatility != null ? metrics : null;
}

function dailyMoveFromMetrics(metrics: RiskMetrics | null): number | null {
  return metrics?.volatility != null
    ? metrics.volatility / Math.sqrt(252)
    : null;
}

export function getAnalyzerOverallRisk(
  metrics: RiskMetrics | null,
  dailyMove: number | null
): AnalyzerOverallRisk | null {
  if (!metrics) return null;

  const clampScore = (value: number) => Math.min(Math.max(value, 0), 1);
  const volScore = clampScore((metrics.volatility ?? 0) / 40);
  const ddScore = clampScore(Math.abs(metrics.maxDrawdown ?? 0) / 30);
  const betaScore = clampScore((metrics.beta ?? 0) / 1.5);
  const dailyScore = clampScore(Math.abs(dailyMove ?? 0) / 3);
  const composite = (volScore + ddScore + betaScore + dailyScore) / 4;
  const level = Math.round(10 + composite * 80);
  const label =
    level < 30 ? "Conservative"
    : level < 45 ? "Low-Moderate"
    : level < 60 ? "Moderate"
    : level < 75 ? "Moderate-Aggressive"
    : "Aggressive";

  return { level, label };
}

export function computeAnalyzerRiskAnalysis(
  strategyFactors: FactorEntry[],
  benchmarkFactors: FactorEntry[]
): AnalyzerRiskAnalysis {
  const metrics = usableMetrics(computeRiskMetrics(strategyFactors, benchmarkFactors));
  const benchmarkMetrics = usableMetrics(computeRiskMetrics(benchmarkFactors, benchmarkFactors));
  const dailyMove = dailyMoveFromMetrics(metrics);
  const benchmarkDailyMove = dailyMoveFromMetrics(benchmarkMetrics);

  return {
    metrics,
    benchmarkMetrics,
    dailyMove,
    benchmarkDailyMove,
    overallRisk: getAnalyzerOverallRisk(metrics, dailyMove),
  };
}

export function useAnalyzerRiskAnalysis(
  strategyFactors: FactorEntry[],
  benchmarkFactors: FactorEntry[]
): AnalyzerRiskAnalysis {
  return useMemo(
    () => computeAnalyzerRiskAnalysis(strategyFactors, benchmarkFactors),
    [strategyFactors, benchmarkFactors]
  );
}
